import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Spin,
  Progress,
  Tabs,
  Table,
  Tag,
  Tooltip,
  Button,
  Typography,
  Space,
  Input,
  Select,
  Avatar,
  Empty,
} from "antd";
import {
  getCampaignAnalytics,
  getCampaignOpenAnalytics,
  getCampaignClickAnalytics,
  getCampaignDeviceAnalytics,
  getCampaignGeographicalData,
  getSentEmailsPaginated,
} from "@api/email/emailCampaign";
import { useParams, useHistory } from "react-router-dom";
import {
  EyeOutlined,
  LinkOutlined,
  DownloadOutlined,
  ReloadOutlined,
  GlobalOutlined,
  MobileOutlined,
  MailOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  DesktopOutlined,
  ArrowLeftOutlined,
  LineChartOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import { format } from "date-fns";
import { debounce } from "lodash";

// Import our components
import CampaignOverview from "./components/CampaignOverview";
import OpenAnalytics from "./components/OpenAnalytics";
import ClickAnalytics from "./components/ClickAnalytics";
import DeviceAnalytics from "./components/DeviceAnalytics";
import GeoAnalytics from "./components/GeoAnalytics";
import { domainColors, SentEmailsDomainCard } from "./components";
import "../../../../assets/css/sentEmailsPage.css";

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const EmailCampaignAnalytics = () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [openData, setOpenData] = useState(null);
  const [clickData, setClickData] = useState(null);
  const [deviceData, setDeviceData] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [sentEmails, setSentEmails] = useState([]);
  const [sentEmailsStats, setSentEmailsStats] = useState(null);
  const [domainStats, setDomainStats] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [activeTab, setActiveTab] = useState("1");
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    status: undefined,
    dateRange: undefined,
    searchText: "",
    domain: undefined,
  });

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [overview, opens, clicks, devices, geo] = await Promise.all([
        getCampaignAnalytics(id),
        getCampaignOpenAnalytics(id),
        getCampaignClickAnalytics(id),
        getCampaignDeviceAnalytics(id),
        getCampaignGeographicalData(id),
      ]);

      setOverview(overview.data);
      setOpenData(opens.data);
      setClickData(clicks.data);
      setDeviceData(devices.data);
      setGeoData(geo.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      message.error("Failed to load campaign analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchSentEmails = async () => {
    try {
      const { status, dateRange, searchText, domain } = filters;
      const { current, pageSize } = pagination;

      const dateFrom = dateRange?.[0]
        ? dateRange[0].startOf("day").toISOString()
        : undefined;
      const dateTo = dateRange?.[1]
        ? dateRange[1].endOf("day").toISOString()
        : undefined;

      const response = await getSentEmailsPaginated(id, {
        page: current,
        perPage: pageSize,
        status,
        dateFrom,
        dateTo,
        searchText: searchText?.trim(),
        domain: domain?.toLowerCase(),
        sortBy: "sentAt",
        sortOrder: "desc",
      });

      if (response.data.success) {
        const {
          sentEmails,
          stats,
          domainStats,
          pagination: paginationData,
        } = response.data.data;
        setSentEmails(sentEmails);
        setSentEmailsStats(stats);
        setDomainStats(domainStats);
        setPagination({
          current: paginationData.currentPage,
          pageSize: paginationData.itemsPerPage,
          total: paginationData.totalItems,
        });
      }
    } catch (error) {
      console.error("Error fetching sent emails:", error);
      message.error("Failed to load sent emails");
    }
  };

  useEffect(() => {
    fetchAnalytics();
    fetchSentEmails();
  }, [id]);

  useEffect(() => {
    fetchSentEmails();
  }, [pagination.current, pagination.pageSize, filters]);

  // Debounced search handler
  const debouncedSearch = debounce((value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, 500);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: undefined,
      dateRange: undefined,
      searchText: "",
      domain: undefined,
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (pagination, filters, sorter) => {
    // Update pagination
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: pagination.total,
    });

    // Update filters based on table filters
    if (filters.status?.length) {
      handleFilterChange("status", filters.status[0]);
    }

    // Handle sort order
    if (sorter.field) {
      // You can implement server-side sorting here if needed
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    await fetchSentEmails();
    setRefreshing(false);
    message.success("Analytics refreshed successfully");
  };

  const handleExportData = () => {
    message.info("Preparing export...");
    // Implement export functionality
  };

  const goBack = () => {
    const basePath = history.location.pathname.includes("/super-admin")
      ? "/super-admin"
      : "/marketing-admin";
    history.push(`${basePath}/email/email-campaigns`);
  };

  // Get status tag class
  const getStatusClass = (status) => {
    return status === "success" ? "se-tag-success" : "se-tag-error";
  };

  // Get status icon
  const getStatusIcon = (status) => {
    return status === "success" ? (
      <CheckCircleOutlined />
    ) : (
      <CloseCircleOutlined />
    );
  };

  // Get device icon
  const getDeviceIcon = (device) => {
    if (!device) return <InfoCircleOutlined />;
    return device.toLowerCase().includes("mobile") ? (
      <MobileOutlined />
    ) : (
      <DesktopOutlined />
    );
  };

  // Extract domain from email
  const extractDomain = (email) => {
    if (!email) return "unknown";
    const parts = email.split("@");
    return parts.length > 1 ? parts[1].split(".")[0].toLowerCase() : "unknown";
  };

  if (loading && !sentEmails.length) {
    return (
      <div className="se-container">
        <div className="se-loading">
          <Spin size="large" />
          <div style={{ marginTop: "20px" }}>Loading campaign analytics...</div>
        </div>
      </div>
    );
  }

  // Sent emails table columns
  const sentEmailsColumns = [
    {
      title: "Recipient",
      key: "recipient",
      render: (_, record) => {
        // Extract the domain from the email
        const domain = extractDomain(record.email);

        return (
          <Tooltip
            title={record.user ? record.user.email : record.email}
            className="se-tooltip"
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Avatar
                style={{
                  backgroundColor: domainColors[domain] || domainColors.other,
                  marginRight: "8px",
                }}
                size="small"
              >
                {(
                  record.user?.name?.charAt(0) ||
                  record.email?.charAt(0) ||
                  "?"
                ).toUpperCase()}
              </Avatar>
              <div>
                <div style={{ fontWeight: "500" }}>
                  {record.user ? record.user.email : record.email}
                </div>
                <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                  {record.sentFrom && <span>Sent from: {record.sentFrom}</span>}
                </div>
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "120px",
      render: (status) => (
        <Tag className={`se-tag ${getStatusClass(status)}`}>
          {getStatusIcon(status)}{" "}
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
      filters: [
        { text: "Success", value: "success" },
        { text: "Failed", value: "failed" },
      ],
    },
    {
      title: "Engagement",
      key: "engagement",
      width: "180px",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip
            title={
              <div className="se-tooltip-content">
                <div className="se-tooltip-title">Open Details</div>
                <p>
                  First Open:{" "}
                  {record.engagement?.firstOpenedAt
                    ? format(new Date(record.engagement.firstOpenedAt), "PPpp")
                    : "Never"}
                </p>
                <p>
                  Last Open:{" "}
                  {record.engagement?.lastOpenedAt
                    ? format(new Date(record.engagement.lastOpenedAt), "PPpp")
                    : "Never"}
                </p>
                <p>Total Opens: {record.engagement?.openCount || 0}</p>
              </div>
            }
            className="se-tooltip"
          >
            <Tag
              className={`se-tag ${
                record.engagement?.opened ? "se-tag-open" : ""
              }`}
            >
              <EyeOutlined /> {record.engagement?.openCount || 0}
            </Tag>
          </Tooltip>
          <Tooltip
            title={
              <div className="se-tooltip-content">
                <div className="se-tooltip-title">Click Details</div>
                <p>
                  First Click:{" "}
                  {record.engagement?.firstClickedAt
                    ? format(new Date(record.engagement.firstClickedAt), "PPpp")
                    : "Never"}
                </p>
                <p>
                  Last Click:{" "}
                  {record.engagement?.lastClickedAt
                    ? format(new Date(record.engagement.lastClickedAt), "PPpp")
                    : "Never"}
                </p>
                <p>
                  Unique Links: {record.engagement?.clickedLinks?.length || 0}
                </p>
              </div>
            }
            className="se-tooltip"
          >
            <Tag
              className={`se-tag ${
                record.engagement?.clicked ? "se-tag-click" : ""
              }`}
            >
              <LinkOutlined /> {record.engagement?.clickCount || 0}
            </Tag>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Device",
      key: "device",
      width: "140px",
      render: (_, record) => (
        <Tooltip
          title={
            <div className="se-tooltip-content">
              <div className="se-tooltip-title">Technical Details</div>
              <p>Client: {record.technical?.emailClient || "Unknown"}</p>
              <p>OS: {record.technical?.operatingSystem || "Unknown"}</p>
              <p>Device: {record.technical?.device || "Unknown"}</p>
            </div>
          }
          className="se-tooltip"
        >
          <Tag className="se-tag se-tag-device">
            {getDeviceIcon(record.technical?.device)}{" "}
            {record.technical?.device || "Unknown"}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: "Domain",
      key: "domain",
      width: "120px",
      render: (_, record) => {
        const domain = extractDomain(record.email);
        return (
          <Tag
            style={{
              backgroundColor: `${
                domainColors[domain] || domainColors.other
              }22`,
              color: domainColors[domain] || domainColors.other,
              border: `1px solid ${domainColors[domain] || domainColors.other}`,
            }}
            className="se-tag"
          >
            <GlobalOutlined /> {domain}
          </Tag>
        );
      },
    },
    {
      title: "Sent At",
      dataIndex: "sentAt",
      key: "sentAt",
      width: "180px",
      render: (date) => (
        <span>
          <CalendarOutlined style={{ marginRight: "5px" }} />
          {format(new Date(date), "PPp")}
        </span>
      ),
      sorter: (a, b) => new Date(a.sentAt) - new Date(b.sentAt),
    },
  ];

  const tabItems = [
    {
      key: "1",
      label: (
        <span>
          <MailOutlined /> Overview
        </span>
      ),
      children: <CampaignOverview overview={overview?.overview} />,
    },
    {
      key: "2",
      label: (
        <span>
          <EyeOutlined /> Opens Analysis
        </span>
      ),
      children: <OpenAnalytics openData={openData} />,
    },
    {
      key: "3",
      label: (
        <span>
          <LinkOutlined /> Click Analysis
        </span>
      ),
      children: <ClickAnalytics clickData={clickData} />,
    },
    {
      key: "4",
      label: (
        <span>
          <MobileOutlined /> Device Analytics
        </span>
      ),
      children: <DeviceAnalytics deviceData={deviceData} />,
    },
    {
      key: "5",
      label: (
        <span>
          <GlobalOutlined /> Geographical Insights
        </span>
      ),
      children: <GeoAnalytics geoData={geoData} />,
    },
  ];

  return (
    <div className="se-container">
      {/* Header */}
      <div className="se-header">
        <Row justify="space-between" align="middle">
          <Col>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={goBack}
              style={{ marginRight: "10px" }}
            >
              Back to Campaigns
            </Button>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined spin={refreshing} />}
                onClick={handleRefresh}
                className="se-btn"
                style={{
                  marginRight: "5px",
                  backgroundColor: "#fff",
                  color: "rgba(0, 0, 0, 0.65)",
                  border: "1px solid #d9d9d9",
                  fontWeight: "500",
                }}
              >
                Refresh
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportData}
                className="se-btn"
                style={{
                  marginRight: "5px",
                  backgroundColor: "#1890ff",
                  color: "white",
                  border: "none",
                  fontWeight: "500",
                  boxShadow: "0 2px 0 rgba(0, 0, 0, 0.045)",
                }}
              >
                Export Data
              </Button>
              <Button
                icon={<MailOutlined />}
                onClick={() => {
                  const basePath = history.location.pathname.includes(
                    "/super-admin"
                  )
                    ? "/super-admin"
                    : "/marketing-admin";
                  history.push(
                    `${basePath}/email/email-campaigns/${id}/sent-emails`
                  );
                }}
                className="se-btn"
                style={{
                  backgroundColor: "#52c41a",
                  color: "white",
                  border: "none",
                  fontWeight: "500",
                  boxShadow: "0 2px 0 rgba(0, 0, 0, 0.045)",
                }}
              >
                View Sent Emails
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div style={{ padding: "0 24px" }}>
        {/* Title */}
        <div style={{ marginBottom: "24px" }}>
          <Title level={2} className="se-title">
            <MailOutlined style={{ marginRight: "10px", color: "#1890ff" }} />
            {overview?.campaignName || "Campaign Analytics"}
          </Title>
          <Text type="secondary" className="se-subtitle">
            Campaign performance metrics and engagement data
          </Text>
        </div>

        {/* Stats Section */}
        <Row gutter={16} style={{ marginBottom: "24px" }}>
          <Col xs={24} md={24} style={{ marginBottom: "12px" }}>
            <Card
              bordered={false}
              className="se-card"
              style={{ background: "#f9f9f9" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <Text strong>Campaign ID:</Text>
                  <Text
                    copyable={{ text: id }}
                    style={{ marginLeft: "8px", fontSize: "16px" }}
                  >
                    {id}
                  </Text>
                </div>
                <Tag
                  color="blue"
                  style={{ fontWeight: "500", textTransform: "uppercase" }}
                >
                  CAMPAIGN IDENTIFIER
                </Tag>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card bordered={false} className="se-card se-stats-card">
              <div className="se-stats-value">
                {overview?.statistics?.total || 0}
              </div>
              <div className="se-stats-title">Total Recipients</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              className="se-card se-stats-card se-stats-success"
            >
              <div className="se-stats-value" style={{ color: "#52c41a" }}>
                {(overview?.statistics?.sent || 0) +
                  (overview?.statistics?.success || 0)}
              </div>
              <div className="se-stats-title">Successfully Delivered</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              className="se-card se-stats-card se-stats-open"
            >
              <div className="se-stats-value" style={{ color: "#1890ff" }}>
                {overview?.overview?.openRate?.toFixed(1) || 0}%
              </div>
              <div className="se-stats-title">Open Rate</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              className="se-card se-stats-card se-stats-click"
            >
              <div className="se-stats-value" style={{ color: "#722ed1" }}>
                {overview?.overview?.clickRate?.toFixed(1) || 0}%
              </div>
              <div className="se-stats-title">Click Rate</div>
            </Card>
          </Col>
        </Row>

        {/* Progress Card */}
        <Card
          bordered={false}
          className="se-card se-progress-card"
          style={{ marginBottom: "24px" }}
        >
          <Row gutter={24} align="middle">
            <Col xs={24} md={8}>
              <div className="se-progress-container">
                <Progress
                  type="circle"
                  percent={(
                    ((overview?.statistics?.sent || 0) /
                      (overview?.statistics?.total || 1)) *
                    100
                  ).toFixed(1)}
                  status={
                    overview?.status === "completed" ? "success" : "active"
                  }
                  width={150}
                  strokeWidth={10}
                  strokeColor={{
                    "0%": "#108ee9",
                    "100%": "#87d068",
                  }}
                />
                <div className="se-progress-title">Campaign Progress</div>
                <Tag
                  className="se-tag"
                  color={
                    overview?.status === "completed" ? "success" : "processing"
                  }
                >
                  {overview?.status
                    ? overview.status.charAt(0).toUpperCase() +
                      overview.status.slice(1)
                    : "Unknown"}
                </Tag>
              </div>
            </Col>
            <Col xs={24} md={16}>
              <div className="se-metrics-grid">
                <div className="se-metric-item">
                  <div className="se-metric-value">
                    {overview?.statistics?.total?.toLocaleString() || 0}
                  </div>
                  <div className="se-metric-label">Total Recipients</div>
                </div>
                <div className="se-metric-item">
                  <div className="se-metric-value">
                    {overview?.statistics?.sent?.toLocaleString() || 0}
                  </div>
                  <div className="se-metric-label">Sent</div>
                </div>
                <div className="se-metric-item">
                  <div className="se-metric-value">
                    {overview?.statistics?.failed?.toLocaleString() || 0}
                  </div>
                  <div className="se-metric-label">Failed</div>
                </div>
                <div className="se-metric-item">
                  <div className="se-metric-value">
                    {overview?.overview?.openCount?.toLocaleString() || 0}
                  </div>
                  <div className="se-metric-label">Opens</div>
                </div>
                <div className="se-metric-item">
                  <div className="se-metric-value">
                    {overview?.overview?.clickCount?.toLocaleString() || 0}
                  </div>
                  <div className="se-metric-label">Clicks</div>
                </div>
                <div className="se-metric-item">
                  <div className="se-metric-value">
                    {overview?.overview?.clickToOpenRate?.toFixed(1) || 0}%
                  </div>
                  <div className="se-metric-label">Click-to-Open</div>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Tabs Content */}
        <Card
          bordered={false}
          className="se-card se-tabs-card"
          style={{ marginBottom: "24px" }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            type="card"
            size="large"
            className="se-tabs"
            animated={{ inkBar: true, tabPane: true }}
          />
        </Card>

        {/* Sent Emails Section */}
        <div style={{ marginBottom: "24px" }}>
          <h3 className="se-section-title">
            <LineChartOutlined style={{ marginRight: "8px" }} />
            Delivery Details
          </h3>

          {/* Filters */}
          <Card
            bordered={false}
            className="se-card se-filters-card"
            style={{ marginBottom: "16px" }}
          >
            <Row gutter={16} className="se-filter-row">
              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                  Status
                </div>
                <Select
                  placeholder="Filter by status"
                  style={{ width: "100%" }}
                  value={filters.status}
                  onChange={(value) => handleFilterChange("status", value)}
                  allowClear
                >
                  <Option value="success">Success</Option>
                  <Option value="failed">Failed</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                  Domain
                </div>
                <Select
                  placeholder="Filter by domain"
                  style={{ width: "100%" }}
                  value={filters.domain}
                  onChange={(value) => handleFilterChange("domain", value)}
                  allowClear
                >
                  {domainStats
                    ? Object.keys(domainStats).map((domain) => (
                        <Option key={domain} value={domain}>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <div
                              style={{
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                backgroundColor:
                                  domainColors[domain] || domainColors.other,
                                marginRight: "8px",
                              }}
                            />
                            {domain.charAt(0).toUpperCase() + domain.slice(1)}
                          </div>
                        </Option>
                      ))
                    : null}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                  Search
                </div>
                <Search
                  placeholder="Search by email"
                  value={filters.searchText}
                  onChange={(e) => debouncedSearch(e.target.value)}
                  style={{ width: "100%" }}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                  Actions
                </div>
                <Button icon={<FilterOutlined />} onClick={clearFilters}>
                  Clear Filters
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Domain Stats */}
          {domainStats && (
            <div style={{ marginBottom: "24px" }}>
              <Row gutter={16}>
                {Object.entries(domainStats).map(([domain, stats]) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={domain}>
                    <SentEmailsDomainCard domain={domain} stats={stats} />
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {/* Email Table */}
          <div className="se-table-container">
            <Table
              columns={sentEmailsColumns}
              dataSource={sentEmails}
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} emails`,
              }}
              onChange={handleTableChange}
              rowKey="_id"
              scroll={{ x: "max-content", y: "calc(100vh - 500px)" }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No emails found"
                  />
                ),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailCampaignAnalytics;
